import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { signOut } from "@/features/auth/api/identity-platform-auth";
import { AUTH_SESSION_QUERY_KEY, fetchAuthSession } from "@/features/auth/api/auth-session-client";
import { useAuth } from "@/features/auth/hooks/use-auth";
import type { AuthSession } from "@/features/auth/model/auth-session";
import { ApiError } from "@/shared/api/api-error";

export function useAuthSession() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  return useQuery<AuthSession, Error>({
    queryKey: AUTH_SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        return await fetchAuthSession();
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === 401) {
          await signOut();
          navigate("/login", { replace: true });
          throw new Error("Authentication expired. Please sign in again.");
        }

        throw error;
      }
    },
    enabled: isAuthenticated,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.statusCode === 401 || error.statusCode === 403)) {
        return false;
      }

      return failureCount < 2;
    },
  });
}
